import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, upper, definitionNotHeld } from './context.js';
import { type TriggerCandidate, type TriggerEvent, type TriggerReaders, TriggerInputUnavailable, VITALITY_STATUS_IDS, collectTriggerCandidates, gateTriggerCandidate, passesDbGate } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { reactorSeesAttacker } from './trigger-executor.js';
import { consentAllows, deferFortunateOffer } from './trigger-consent.js';
import { applyStatusEffect } from './statuses/apply-status-effect.js';
import { getStatusEffectDurationTimes } from './statuses/get-status-effect-duration-times.js';
import { abilityChargeEntry } from './charges/charge-entry.js';
import { adjustCharges } from './charges/adjust-charges.js';

const EVADE = 'BE4CCA95-33F0-323D-BA54-844D2540C0F0';
const FORTUNATE = 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8';
const EVADE_LAYERS = new Set(['range', 'effect_ends_on_start_of_caster_next_turn',
  'if_caster_has_status_effect_not_active', 'triggers_on_roll_type_against_the_caster',
  'roll_type_against_dis', 'triggers_on_next_roll_type_against', 'ability_charges', 'recharge_range_begin', 'recharge_range_end']);
/** damage_roll.php's pre-roll defensive pass: restrict_to_abilities_with_layer / exclude_abilities_with_layer (verbatim). */
const PRE_ROLL_RESTRICT_LAYERS = ['roll_type_against_dis', 'roll_type_against_adv', 'triggers_on_next_roll_type_against'];
const PRE_ROLL_EXCLUDE_LAYERS = ['triggers_on_hit', 'triggers_on_miss', 'triggers_on_critical_hit', 'triggers_on_not_a_critical_hit',
  'triggers_on_roll_type_against_the_caster_and_hits', 'triggers_on_next_roll_type_against_and_hits', 'triggers_when_caster_takes_any_damage',
  'triggers_when_caster_takes_damage_type'];
const entryId = (value: unknown): string => upper(itemField(record(value), 'item_id') ?? value);

/** janus_trigger_inject_universal_fortunate() after caster_gains_luck(). */
export function applyUniversalFortunateOnLuckGain(ctx: JanusCtx, creature: Row): void {
  if (!passesDbGate(ctx.read, FORTUNATE, creature, null)) return;
  if (requiredInt(ecLean(creature, 'reaction'), 'caster_gains_luck:reaction-not-held') <= 0) return;
  if (requiredInt(ecLean(creature, 'luck'), 'caster_gains_luck:luck-not-held') <= 0) return;
  // A Fortunate already held or pending: Fortunate's own if_caster_has_status_effect_not_active gate — nothing happens (no reaction, no luck, no
  // row). FRONT-END RULE (owner 2026-09-28): PHP still spent the reaction and 1 luck for nothing (oracle `arm_mech_combat_fortunate_held`).
  if (activeStatuses(ctx, creature).some((row) => upper(row.status_item_id) === FORTUNATE)) return;
  // OWNER RULING 2026-10-03 (trigger-consent.ts deferFortunateOffer): a PLAYER-controlled creature is offered Fortunate AFTER the use (one
  // prompt per gain, never a pre-roll question); nothing fires or is spent here. An NPC / GM creature and the census auto-fire as PHP.
  if (deferFortunateOffer(ctx, creature)) return;
  const fortunateDefinition = ctx.read.definition(FORTUNATE);
  // the id rides the reason so a caller's preflight fetch loop can hold it (lane A4 — N5b's combat-start planner `missingFrom`)
  if (!fortunateDefinition) handBack(`caster_gains_luck:fortunate-definition-not-held:${FORTUNATE}`);
  const duration = getStatusEffectDurationTimes(ctx, fortunateDefinition, creature);
  // a controller the page cannot name ('?') keeps A10's pre-ask (the relay decides: a player's 5 s card, or automatic)
  if (!consentAllows(ctx, creature, fortunateDefinition, FORTUNATE, 'caster_gains_luck', 'reaction_ability')) return;
  insertRoll(ctx, { roll_type_1: 'no_roll', roll_type_2: 'reaction_ability', roll_item_id: FORTUNATE,
    by: creature, to: creature });
  ctx.rows[ctx.rows.length - 1]!.roll_private = 0;
  ec(creature).luck = requiredInt(ecLean(creature, 'luck'), 'caster_gains_luck:luck-not-held') - 1;
  ec(creature).reaction = 0;
  // Use the ordinary self-status applicator: gain listeners, immunity, replacement and pending definitions share one contract.
  applyStatusEffect(ctx, { casterId: ecId(creature), targetIds: [ecId(creature)], abilityId: FORTUNATE,
    rawDiceMod: 0, rawDiceAdvDisStacks: 0, castingTargets: '', useAbilityVariable1: null, useAbilityVariable2: null },
    fortunateDefinition, FORTUNATE, 'reaction_ability_applied', creature, creature,
    duration, true, false);
  insertRoll(ctx, { roll_type_1: 'reaction_ability_applied', roll_item_id: FORTUNATE,
    by: creature, to: creature });
  ctx.rows[ctx.rows.length - 1]!.roll_private = 0;
}

/** Prove the standalone payload and its live gates before it consumes the pre-roll pass's reaction budget. */
function preRollEvadeReady(ctx: JanusCtx, target: Row, rollType: string, attacker: Row,
  candidate: TriggerCandidate, read: TriggerReaders): boolean {
  if (candidate.pool !== 'carrier') handBack('janus_trigger:other-pre-roll-reaction');
  const abilityId = upper(candidate.abilityId);
  if (abilityId !== EVADE && !ctx.read.has(candidate.ability, 'triggers_on_next_roll_type_against'))
    handBack('janus_trigger:other-pre-roll-reaction');
  // The dispatch aims at the inciting attacker (a reaction carrier with no AoE); validate_and_adjust_targets drops an attacker the reactor
  // cannot see (directed visibility) before the Self retarget — Evade then silently never fires (no rows, no spend). A reactor without
  // nightsight in the dark A2 cell: tools/probes/ability_evade_carrier_probe.php (no rows for the dummy creature, rows for a catalog one).
  if (ecId(attacker) !== ecId(target) && !reactorSeesAttacker(ctx, target, attacker)) return false;
  const definition = candidate.ability;
  const evade = abilityId === EVADE ? definition : ctx.read.definition(EVADE);
  if (!evade) definitionNotHeld('janus_trigger:evade-definition-not-held', EVADE);
  if (Object.keys(record(definition.item_layer_types) ?? {}).some((key) => !EVADE_LAYERS.has(key))
      || ['roll_type_against_dis', 'triggers_on_next_roll_type_against'].some((key) => {
        const actual = new Set(ctx.read.entries(definition, key).map(entryId));
        const expected = new Set(ctx.read.entries(evade, key).map(entryId));
        return !actual.has(upper(rollType)) || actual.size !== expected.size || [...actual].some(id => !expected.has(id));
      }))
    handBack('janus_trigger:evade-contract-not-modeled');
  const forbidden = ctx.read.entries(definition, 'if_caster_has_status_effect_not_active').map(entryId);
  const needsVitality = forbidden.some((id) => VITALITY_STATUS_IDS.has(id));
  const vitality = needsVitality ? upper(read.vStatusId(target)) : '';
  if (needsVitality && !vitality) handBack('janus_trigger:evade-vitality-not-held');
  const held = read.statusItemIds(target);
  if (forbidden.some(id => VITALITY_STATUS_IDS.has(id) && vitality === id || held.includes(id))) return false;
  const inCombat = ctx.read.inCombat(ctx.encounter, target);
  if (inCombat && requiredInt(ecLean(target, 'reaction'), 'janus_trigger:reaction-not-held') <= 0) return false;
  const charged = ctx.read.has(definition, 'ability_charges');
  // Prove the shared debit's reads before any log/status/resource write or consent request.
  if (charged) {
    abilityChargeEntry(ctx, target, abilityId);
    if (record(target.inventory)?.inventory_id != null) handBack('adjust_charges:inventory-holder');
    if (!ecId(target)) handBack('adjust_charges:holder-not-held');
  }
  // OWNER RULING 2026-10-02 (trigger-consent.ts): Evade is a reaction — a player-controlled target uses it only on its controller's yes
  return consentAllows(ctx, target, definition, abilityId, 'triggers_on_roll_type_against_the_caster', candidate.carrier);
}

/** The standalone pre-roll Evade-shaped dispatch for attack rolls. */
export function applyPreRollEvade(ctx: JanusCtx, target: Row, rollType: string, attacker: Row): boolean {
  if (!ctx.read.reactionInventoryComplete(target)) handBack('janus_trigger:reaction-inventory-incomplete');
  const abilities = record(record(record(target.computed)?.creature)?.abilities);
  const compiled = abilities?.reaction_ability;
  if (compiled != null && !Array.isArray(compiled)) handBack('janus_trigger:reaction-state-not-held');
  // The compiled bucket UNION the root grants (universal Evade) supplies carriers; each pool is already PL-ranked.
  // Gate subsequent contenders against a spent reaction only after visibility, conditions, charge proof and consent allow the first.
  const base = janusCtxTriggerReaders(ctx);
  let reactionLeft: number | null = null, prepared = false;
  const read = { ...base, scalar: (row: Row, field: 'reaction' | 'mana'): number =>
    field === 'reaction' && ecId(row) === ecId(target) && reactionLeft !== null ? reactionLeft : base.scalar(row, field) };
  const event: TriggerEvent = { trigger: 'triggers_on_roll_type_against_the_caster', rollType: upper(rollType), hit: null,
    restrictToAbilitiesWithLayer: PRE_ROLL_RESTRICT_LAYERS, excludeAbilitiesWithLayer: PRE_ROLL_EXCLUDE_LAYERS };
  const fired: TriggerCandidate[] = [];
  try {
    const { caster, carriers, statuses } = collectTriggerCandidates(read, target, event);
    for (const candidate of [...carriers, ...statuses]) {
      // Held status/passive modifiers run in skillRollArr's current roll frame.
      if (candidate.pool === 'status' || candidate.carrier === 'passive_ability') continue;
      if (fired.some(prior => upper(prior.abilityId) === upper(candidate.abilityId))) continue;
      if (!gateTriggerCandidate(read, candidate, caster, event, attacker).fire) continue;
      const spends = candidate.competes && base.inCombat(caster);
      if (spends && !preRollEvadeReady(ctx, target, rollType, attacker, candidate, base)) continue;
      fired.push(candidate);
      if (spends) { reactionLeft = 0; prepared = true; }
    }
  } catch (error) {
    if (error instanceof TriggerInputUnavailable) handBack(`janus_trigger:${error.reason}`);
    throw error;
  }
  if (!fired.length) return false;
  if (fired.length > 1) handBack('janus_trigger:other-pre-roll-reaction');
  const candidate = fired[0]!;
  if (!prepared && !preRollEvadeReady(ctx, target, rollType, attacker, candidate, base)) return false;
  const abilityId = upper(candidate.abilityId), charged = ctx.read.has(candidate.ability, 'ability_charges');
  if (charged) adjustCharges(ctx, target, abilityId, -1, false);
  // The applied marker is the reaction's single log entry; a no-roll prelude
  // renders identically and made one Evade look like two activations.
  insertRoll(ctx, { roll_type_1: 'reaction_ability_applied', roll_item_id: abilityId, by: target, to: target });
  if (ctx.read.inCombat(ctx.encounter, target)) ec(target).reaction = 0;
  // PHP applies Evade as a status on the holder, live until a next-roll-against consumes it (lane A8 — read by a later is_target roll)
  (ctx.preRollEvadeStatus ??= new Set()).add(ecId(target));
  return true;
}
